/**
 * ProfileSyncServiceCard - карточка обновления данных профиля с сервера.
 * Выдержана в единой стилистике страницы Профиля (фон #f8fafc, четкая кнопка).
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  SyncOutlined,
  CheckCircleOutline
} from '@mui/icons-material';
import { ZERDE_COLORS } from '../../constants';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../../admin/reports/edit/editApi';
import { serviceButtonFullSx, serviceRowSx } from './serviceCardStyles';

export interface ProfileSyncServiceCardProps {
  username: string;
  /** Перечитать профиль и статусы обязательных разделов с сервера */
  onSyncData: () => Promise<void>;
}

const ProfileSyncServiceCard: React.FC<ProfileSyncServiceCardProps> = ({
  username,
  onSyncData
}) => {
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);

  const handleSyncProfile = async () => {
    if (!username) return;
    setSyncing(true);
    try {
      await onSyncData();
      setSynced(true);
      notify.success('Данные профиля обновлены');
      setTimeout(() => setSynced(false), 3000);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось обновить данные профиля'));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={serviceRowSx}>
      {/* Левая часть: Иконка + Заголовок + Простое объяснение */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: 'rgba(86, 191, 132, 0.1)',
            color: ZERDE_COLORS.emerald,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <SyncOutlined sx={{ fontSize: 24 }} />
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
            Обновление данных профиля
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '0.85rem' }}>
            Нажмите, если вам только что изменили номер аптеки, должность или права в офисе, и нужно применить их без перезахода в систему.
          </Typography>
        </Box>
      </Box>

      {/* Правая часть: Кнопка действия */}
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip title="Запросить свежие данные из общей базы Zerde" arrow>
          <Button
            variant="outlined"
            startIcon={
              syncing ? (
                <CircularProgress size={16} color="inherit" />
              ) : synced ? (
                <CheckCircleOutline />
              ) : (
                <SyncOutlined />
              )
            }
            disabled={syncing}
            onClick={handleSyncProfile}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              py: 0.85,
              px: 2,
              borderColor: synced ? '#86efac' : '#cbd5e1',
              bgcolor: synced ? '#f0fdf4' : '#ffffff',
              color: synced ? '#16a34a' : '#334155',
              whiteSpace: 'nowrap',
              ...serviceButtonFullSx,
              '&:hover': {
                borderColor: '#10b981',
                bgcolor: '#f0fdf4',
                color: '#15803d'
              }
            }}
          >
            {syncing ? 'Обновление...' : synced ? 'Обновлено' : 'Обновить данные'}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ProfileSyncServiceCard;
